import { useState, useEffect, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Autocomplete,
  Button,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { COLORS } from "../theme/tokens";
import api from "../api";
import { useDomains, useServers, useCategories } from "../hooks/queries";

// Flat spec design tokens (light-only).
const colors = {
  text: COLORS.ink,
  textMuted: COLORS.muted,
  textFaint: COLORS.faint,
  cardBg: COLORS.surface,
  cardBorder: COLORS.hairline,
  cardBorderHover: COLORS.borderHover,
  hover: COLORS.tile,
  divider: COLORS.hairline,
};

// Small coloured pill for a fetched value (IP, SSL days, cert, up/down).
// tone: "info" (blue) · "ok" (green) · "bad" (red) · "muted" (grey).
function StatChip({ label, value, tone = "info" }) {
  const tones = {
    info: { fg: "#1D4ED8", bg: "#EFF6FF" },
    ok: { fg: "#15803D", bg: "#DCFCE7" },
    bad: { fg: "#B91C1C", bg: "#FEE2E2" },
    muted: { fg: COLORS.muted, bg: COLORS.tile },
  };
  const t = tones[tone] || tones.info;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "baseline",
        gap: 0.75,
        px: 1.25,
        py: 0.6,
        borderRadius: "8px",
        background: t.bg,
        border: `1px solid ${COLORS.hairline}`,
      }}
    >
      <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.4px", textTransform: "uppercase", color: COLORS.muted }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.fg, fontFamily: "monospace" }}>
        {value}
      </Typography>
    </Box>
  );
}

// Add / Edit Sub-Domain form body (spec §5.2): Parent Domain (select) ·
// Sub-domain Name (short text) · Server (select) · IP Address (text). The full
// name = `<sub>.<parent>` is built on submit and the parent's id is sent so the
// row links correctly. Lives inside AddSubdomainModal.
function AddSubdomainForm({
  onSuccess,
  onCancel,
  editId = null,
  initialName = "",
  initialIp = "",
  initialPublicIp = "",
  initialPrivateIp = "",
  initialParentId = null,
  initialServerId = null,
  initialCategoryId = null,
  initialSslValidity = null,
  initialSslStatus = "",
  initialStatus = "",
}) {
  const queryClient = useQueryClient();
  const isEdit = editId != null;

  const { data: domainsData } = useDomains();
  const domains = useMemo(
    () => (Array.isArray(domainsData) ? domainsData : domainsData?.rows || []),
    [domainsData]
  );
  const { data: serversData } = useServers();
  const servers = Array.isArray(serversData)
    ? serversData
    : serversData?.rows || [];
  const { data: categoriesData } = useCategories();
  const categories = Array.isArray(categoriesData)
    ? categoriesData
    : categoriesData?.rows || [];

  const [parentId, setParentId] = useState(
    initialParentId != null ? String(initialParentId) : ""
  );
  const [shortName, setShortName] = useState("");
  // Server + category are the subdomain's OWN links (not inherited) — stored by id.
  const [serverId, setServerId] = useState(
    initialServerId != null ? String(initialServerId) : ""
  );
  const [categoryId, setCategoryId] = useState(
    initialCategoryId != null ? String(initialCategoryId) : ""
  );
  // IPs are derived from the selected server's DB record; user can override.
  const [publicIp, setPublicIp] = useState(initialPublicIp || initialIp || "");
  const [privateIp, setPrivateIp] = useState(initialPrivateIp || "");
  const [publicIpManuallyEdited, setPublicIpManuallyEdited] = useState(false);
  const [privateIpManuallyEdited, setPrivateIpManuallyEdited] = useState(false);
  // SSL validity stays empty unless the user enters it manually.
  const [sslValidity, setSslValidity] = useState(
    initialSslValidity != null ? String(initialSslValidity) : ""
  );
  const [sslStatus] = useState(initialSslStatus || "");
  const [status, setStatus] = useState(initialStatus || "Unknown");
  const [fetching, setFetching] = useState(false);
  const [fetchNote, setFetchNote] = useState("");
  // Snapshot of the last live check, shown as chips under the name field.
  const [fetched, setFetched] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const parent = useMemo(
    () => domains.find((d) => String(d.id) === String(parentId)) || null,
    [domains, parentId]
  );

  // When the user picks a server, auto-fill the IP from the server's DB record
  // (unless the user has already typed a custom IP).
  useEffect(() => {
    if (isEdit) return; // don't override IPs on edit mode from server change
    if (publicIpManuallyEdited && privateIpManuallyEdited) return;
    if (!serverId) {
      if (!publicIpManuallyEdited) setPublicIp("");
      if (!privateIpManuallyEdited) setPrivateIp("");
      return;
    }
    const server = servers.find((s) => String(s.id) === String(serverId));
    // Mirror the selected server exactly. A server with no IP on record must
    // CLEAR the field — previously it was left untouched, so the IP of the
    // previously selected server stayed behind and got saved (still labelled
    // "· from server") against a server that never had it.
    if (!publicIpManuallyEdited) {
      setPublicIp(server?.publicIp || "");
    }
    if (!privateIpManuallyEdited) {
      setPrivateIp(server?.privateIp || "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverId, servers]);

  // Edit mode: once the domain list is available, split the stored full name
  // (e.g. "crm.example.com") into the short label ("crm") shown in the field.
  const didInitShort = useRef(false);
  useEffect(() => {
    if (!isEdit || didInitShort.current || !initialName) return;
    if (!domains.length) return;
    const p = domains.find((d) => String(d.id) === String(initialParentId));
    const pName = (p?.name || "").toLowerCase();
    const full = initialName.toLowerCase();
    if (pName && full.endsWith("." + pName)) {
      setShortName(initialName.slice(0, initialName.length - pName.length - 1));
    } else {
      setShortName(initialName.split(".")[0]);
    }
    didInitShort.current = true;
  }, [isEdit, initialName, initialParentId, domains]);

  // The server is NOT inherited from the parent — a subdomain can sit on a
  // different host. The user picks it themselves.
  function handleParentChange(value) {
    setParentId(value);
  }

  const fullPreview =
    shortName.trim() && parent
      ? `${shortName.trim().toLowerCase().replace(/\.+$/, "")}.${parent.name}`
      : "";

  // Fetch only checks the live status (Up/Down) for the full subdomain hostname.
  // IP comes from the server selection (DB). SSL validity is left for the user.
  async function handleFetch() {
    if (!parent) {
      setSubmitError("Select a parent domain first");
      return;
    }
    const short = shortName.trim().toLowerCase().replace(/\.+$/, "");
    if (!short) {
      setSubmitError("Enter a sub-domain name first (e.g. crm, app, mail)");
      return;
    }
    const full = `${short}.${parent.name}`;
    setFetching(true);
    setSubmitError("");
    setFetchNote("");
    setFetched(null);
    try {
      const info = await api.get(`/subdomains/lookup?name=${encodeURIComponent(full)}`);
      if (!info.found) {
        setSubmitError(info.message || "Couldn't look this sub-domain up.");
        return;
      }
      // Only update status from lookup — IP comes from server DB, SSL is manual.
      if (info.status) setStatus(info.status);
      setFetched({
        status: info.status || "",
      });
      setFetchNote("Status checked live");
    } catch (err) {
      setSubmitError(err.message || "Lookup failed. Please try again.");
    } finally {
      setFetching(false);
    }
  }

  async function handleSubmit() {
    setSubmitError("");
    if (!parent) {
      setSubmitError("Select a parent domain");
      return;
    }
    const short = shortName.trim().toLowerCase().replace(/\.+$/, "");
    if (!short) {
      setSubmitError("Enter a sub-domain name (e.g. crm, app, mail)");
      return;
    }
    const payload = {
      name: `${short}.${parent.name}`,
      parentDomainId: parent.id,
      serverId: serverId || null,
      categoryId: categoryId || null,
      publicIp: publicIp || null,
      privateIp: privateIp || null,
      ip: publicIp || null,
      sslValidity: sslValidity === "" ? null : Number(sslValidity),
      sslStatus: sslStatus || null,
      status: status || "Unknown",
    };
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/subdomains/${editId}`, payload);
      } else {
        await api.post("/subdomains", payload);
      }
      queryClient.invalidateQueries({ queryKey: ["subdomains"] });
      queryClient.invalidateQueries({ queryKey: ["domains"] });
      onSuccess?.();
    } catch (err) {
      setSubmitError(
        err.message || `Failed to ${isEdit ? "update" : "add"} subdomain`
      );
      setSubmitting(false);
    }
  }

  const labelSx = {
    display: "block",
    fontSize: 12.5,
    fontWeight: 600,
    color: colors.textMuted,
    mb: 0.75,
  };
  const groupLabelSx = {
    display: "block",
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.7px",
    textTransform: "uppercase",
    color: colors.textFaint,
    mb: 1.75,
  };
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: "#fff",
      color: colors.text,
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: COLORS.primary },
    },
    "& input": { color: colors.text },
    "& .MuiSelect-select": { color: colors.text },
    "& input::placeholder": { color: colors.textFaint, opacity: 1 },
  };
  const selectMenuProps = {
    // Open the list BELOW the field (not overlapping it like a native select)
    // and cap its height so a long domain list scrolls instead of overflowing.
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
    // maxHeight as an INLINE style so it always wins over MUI's computed value.
    PaperProps: {
      style: { maxHeight: 300 },
      sx: {
        mt: 0.5,
        background: "#fff",
        border: `1px solid ${colors.cardBorder}`,
        boxShadow: "0 12px 32px rgba(16, 24, 40, 0.14)",
        "& .MuiMenuItem-root": { color: colors.text, fontSize: 14 },
        "& .MuiMenuItem-root:hover": { background: colors.hover },
        "& .MuiMenuItem-root.Mui-selected": { background: COLORS.primarySoft },
      },
    },
  };

  return (
    <Stack spacing={2.5}>
      {submitError && (
        <Alert severity="error" onClose={() => setSubmitError("")}>
          {submitError}
        </Alert>
      )}
      {fetchNote && (
        <Alert severity="success" onClose={() => setFetchNote("")}>
          {fetchNote}
        </Alert>
      )}

      {/* Parent Domain — searchable (type to filter the long domain list) */}
      <Box>
        <Typography component="label" sx={labelSx}>
          Parent Domain
        </Typography>
        <Autocomplete
          options={domains}
          value={parent}
          onChange={(e, val) => handleParentChange(val ? String(val.id) : "")}
          getOptionLabel={(o) => o?.name || ""}
          isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
          fullWidth
          autoHighlight
          slotProps={{
            paper: {
              sx: {
                mt: 0.5,
                background: "#fff",
                border: `1px solid ${colors.cardBorder}`,
                boxShadow: "0 12px 32px rgba(16, 24, 40, 0.14)",
              },
            },
            listbox: {
              sx: {
                maxHeight: 300,
                "& .MuiAutocomplete-option": { fontSize: 14, color: colors.text },
                "& .MuiAutocomplete-option.Mui-focused": { background: colors.hover },
                "& .MuiAutocomplete-option[aria-selected='true']": { background: COLORS.primarySoft },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search a domain…"
              sx={fieldSx}
            />
          )}
        />
      </Box>

      {/* Sub-domain Name — short text + Fetch button inline */}
      <Box>
        <Typography component="label" sx={labelSx}>
          Sub-domain Name
        </Typography>
        <TextField
          placeholder="e.g. crm, app, mail"
          value={shortName}
          onChange={(e) => setShortName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleFetch();
            }
          }}
          fullWidth
          sx={fieldSx}
          slotProps={{
            input: parent
              ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography
                          sx={{ fontSize: 13, color: colors.textFaint, whiteSpace: "nowrap" }}
                        >
                          .{parent.name}
                        </Typography>
                        <Button
                          onClick={handleFetch}
                          disabled={fetching || !shortName.trim()}
                          disableElevation
                          startIcon={
                            fetching ? (
                              <CircularProgress size={14} sx={{ color: "#fff" }} />
                            ) : (
                              <TravelExploreIcon sx={{ fontSize: 17 }} />
                            )
                          }
                          sx={{
                            flexShrink: 0,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: 12.5,
                            borderRadius: "8px",
                            px: 1.5,
                            whiteSpace: "nowrap",
                            color: "#fff",
                            background: COLORS.primary,
                            "&:hover": { background: "#1D4ED8" },
                            "&.Mui-disabled": { color: "rgba(255,255,255,0.7)", background: "#BFD3F2" },
                          }}
                        >
                          {fetching ? "…" : "Fetch"}
                        </Button>
                      </Stack>
                    </InputAdornment>
                  ),
                }
              : undefined,
          }}
        />
        {fullPreview && (
          <Typography sx={{ fontSize: 12.5, color: colors.textMuted, mt: 1 }}>
            Will create:{" "}
            <Box component="span" sx={{ color: COLORS.primary, fontWeight: 700 }}>
              {fullPreview}
            </Box>
          </Typography>
        )}
        {/* Fetched-result chips — status chip only (IP comes from server, SSL is manual) */}
        {fetched && (
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} sx={{ mt: 1.5 }}>
            {fetched.status && (
              <StatChip
                label="Status"
                value={fetched.status}
                tone={fetched.status === "Active" ? "ok" : fetched.status === "Down" ? "bad" : "muted"}
              />
            )}
          </Stack>
        )}
      </Box>

      {/* Server · Category · IP · SSL — the subdomain's own links (not inherited) */}
      <Box sx={{ pt: 0.5, borderTop: `1px solid ${colors.divider}` }}>
        <Typography component="span" sx={{ ...groupLabelSx, mt: 2 }}>
          Server · Category · DNS
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2.5,
          }}
        >
          {/* Server — select (by id). Selecting a server auto-fills IP from DB. */}
          <Box>
            <Typography component="label" sx={labelSx}>
              Server
            </Typography>
            <TextField
              select
              fullWidth
              value={serverId}
              onChange={(e) => {
                setServerId(e.target.value);
                setPublicIpManuallyEdited(false);
                setPrivateIpManuallyEdited(false);
              }}
              sx={fieldSx}
              SelectProps={{ displayEmpty: true, MenuProps: selectMenuProps }}
            >
              <MenuItem value="">
                <Box component="span" sx={{ color: colors.textFaint }}>
                  None
                </Box>
              </MenuItem>
              {servers.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name || "(unnamed)"}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Category — select (by id, the sub-domain's own site type) */}
          <Box>
            <Typography component="label" sx={labelSx}>
              Category
            </Typography>
            <TextField
              select
              fullWidth
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              sx={fieldSx}
              SelectProps={{ displayEmpty: true, MenuProps: selectMenuProps }}
            >
              <MenuItem value="">
                <Box component="span" sx={{ color: colors.textFaint }}>
                  None
                </Box>
              </MenuItem>
              {categories.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Public IP — auto-filled from the selected server's DB record, editable */}
          <Box>
            <Typography component="label" sx={labelSx}>
              Public IP
              {serverId && publicIp && (
                <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: COLORS.primary }}>
                  · from server
                </Box>
              )}
            </Typography>
            <TextField
              placeholder="e.g. 8.8.8.8"
              value={publicIp}
              onChange={(e) => {
                setPublicIp(e.target.value);
                setPublicIpManuallyEdited(true);
              }}
              fullWidth
              sx={fieldSx}
            />
          </Box>

          {/* Private IP — auto-filled from the selected server's DB record, editable */}
          <Box>
            <Typography component="label" sx={labelSx}>
              Private IP
              {serverId && privateIp && (
                <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 600, color: COLORS.primary }}>
                  · from server
                </Box>
              )}
            </Typography>
            <TextField
              placeholder="e.g. 192.168.1.10"
              value={privateIp}
              onChange={(e) => {
                setPrivateIp(e.target.value);
                setPrivateIpManuallyEdited(true);
              }}
              fullWidth
              sx={fieldSx}
            />
          </Box>

          {/* SSL Validity — user enters manually; leave empty if not known */}
          <Box>
            <Typography component="label" sx={labelSx}>
              SSL Validity (Days)
              {sslStatus && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    color:
                      sslStatus === "valid"
                        ? "#16A34A"
                        : sslStatus === "invalid"
                        ? "#DC2626"
                        : colors.textFaint,
                  }}
                >
                  · {sslStatus}
                </Box>
              )}
            </Typography>
            <TextField
              type="number"
              placeholder="e.g. 90"
              value={sslValidity}
              onChange={(e) => setSslValidity(e.target.value)}
              fullWidth
              inputProps={{ min: 0 }}
              sx={fieldSx}
            />
          </Box>
        </Box>
      </Box>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        sx={{ pt: 2, borderTop: `1px solid ${colors.divider}` }}
      >
        <Button
          type="button"
          onClick={onCancel}
          sx={{
            textTransform: "none",
            color: colors.textMuted,
            px: 3,
            py: 1.25,
            borderRadius: "10px",
            "&:hover": { background: colors.hover, color: colors.text },
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !parent || !shortName.trim()}
          startIcon={
            submitting ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null
          }
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            px: 3.5,
            py: 1.25,
            borderRadius: "10px",
            color: "#fff",
            background: COLORS.primary,
            boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
            "&:hover": {
              boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.7)",
              background: "rgba(59,130,246,0.55)",
            },
            transition: "all 0.15s ease",
          }}
        >
          {submitting
            ? isEdit
              ? "Saving…"
              : "Adding…"
            : isEdit
            ? "Save Changes"
            : "Add Sub-Domain"}
        </Button>
      </Stack>
    </Stack>
  );
}

export default AddSubdomainForm;
